import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import API_URL, { BASE_URL } from '../../../../config';
import './MediaWatermark.css';

function MediaWatermark({ isOpen, onClose, mediaFile, mediaPreview, onSave }) {
    const [watermarkFile, setWatermarkFile] = useState(null);
    const [watermarkPreview, setWatermarkPreview] = useState(null);
    const [watermarkPosition, setWatermarkPosition] = useState('bottom-right');
    const [watermarkSize, setWatermarkSize] = useState(100);
    const [watermarkOpacity, setWatermarkOpacity] = useState(80);
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    const [isProcessing, setIsProcessing] = useState(false);
    const [loadedImageData, setLoadedImageData] = useState(null);

    // Markalar için state
    const [brands, setBrands] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [selectedBrandLogo, setSelectedBrandLogo] = useState(null);

    const imageRef = useRef(null);
    const iconInputRef = useRef(null);
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const watermarkImageRef = useRef(null);

    // Markaları yükle
    useEffect(() => {
        if (isOpen) {
            fetchBrands();
        }
    }, [isOpen]);

    const fetchBrands = async () => {
        try {
            setLoadingBrands(true);
            const response = await axios.get(`${API_URL}/brands`);
            const activeBrands = (response.data.brands || []).filter(
                brand => brand.is_active && (brand.logo_dark_url || brand.logo_light_url)
            );
            setBrands(activeBrands);
        } catch (error) {
            console.error('Brands fetch error:', error);
        } finally {
            setLoadingBrands(false);
        }
    };

    // Mevcut sunucu resmini yükle
    useEffect(() => {
        const loadImage = async () => {
            if (!isOpen) return;

            const imageUrl = mediaPreview?.url || mediaPreview;
            if (!imageUrl) return;

            if (imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')) {
                setLoadedImageData(imageUrl);
                return;
            }

            try {
                const response = await fetch(imageUrl);
                const blob = await response.blob();

                const reader = new FileReader();
                reader.onloadend = () => {
                    setLoadedImageData(reader.result);
                };
                reader.readAsDataURL(blob);
            } catch (error) {
                console.error('Image load error:', error);
                setLoadedImageData(imageUrl);
            }
        };

        loadImage();
    }, [isOpen, mediaPreview]);

    // ESC tuşu
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.keyCode === 27 && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
        }

        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    // Modal kapandığında state'leri sıfırla
    useEffect(() => {
        if (!isOpen) {
            setWatermarkFile(null);
            setWatermarkPreview(null);
            setWatermarkPosition('bottom-right');
            setWatermarkSize(100);
            setWatermarkOpacity(80);
            setSelectedBrandLogo(null);
            setLoadedImageData(null);
        }
    }, [isOpen]);

    // Resim yüklendiğinde
    const handleImageLoad = () => {
        if (imageRef.current) {
            setImageDimensions({
                width: imageRef.current.naturalWidth,
                height: imageRef.current.naturalHeight
            });
        }
    };

    // Icon dosyası seçildiğinde
    const handleIconSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                alert('❌ Sadece resim dosyaları kullanılabilir');
                e.target.value = null;
                return;
            }

            setWatermarkFile(file);
            setSelectedBrandLogo(null);

            const reader = new FileReader();
            reader.onloadend = () => {
                setWatermarkPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Marka logosu seçildiğinde
    const handleBrandLogoSelect = async (brand, logoType) => {
        const logoUrl = logoType === 'dark' ? brand.logo_dark_url : brand.logo_light_url;
        if (!logoUrl) return;

        try {
            const fullUrl = `${BASE_URL}${logoUrl}`;

            const response = await fetch(fullUrl);
            const blob = await response.blob();
            const fileName = logoUrl.split('/').pop();
            const file = new File([blob], fileName, { type: blob.type });

            setWatermarkFile(file);
            setWatermarkPreview(fullUrl);
            setSelectedBrandLogo({ brandId: brand.id, type: logoType });
            // Markanın varsayılan watermark ayarlarını uygula
            if (brand.default_wm_position) setWatermarkPosition(brand.default_wm_position);
            if (brand.default_wm_size) setWatermarkSize(brand.default_wm_size);
            if (brand.default_wm_opacity) setWatermarkOpacity(brand.default_wm_opacity);

            if (iconInputRef.current) {
                iconInputRef.current.value = null;
            }
        } catch (error) {
            console.error('Brand logo select error:', error);
            alert('Logo yüklenirken hata oluştu');
        }
    };

    // Icon kaldır
    const handleRemoveIcon = () => {
        setWatermarkFile(null);
        setWatermarkPreview(null);
        setSelectedBrandLogo(null);
        if (iconInputRef.current) {
            iconInputRef.current.value = null;
        }
    };

    // Pozisyon hesaplama
    const getWatermarkStyle = () => {
        const size = Math.max(30, watermarkSize * 0.4);
        const padding = 15;

        const positionStyles = {
            'top-left': { top: padding, left: padding },
            'top-center': { top: padding, left: '50%', transform: 'translateX(-50%)' },
            'top-right': { top: padding, right: padding },
            'center-left': { top: '50%', left: padding, transform: 'translateY(-50%)' },
            'center': { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
            'center-right': { top: '50%', right: padding, transform: 'translateY(-50%)' },
            'bottom-left': { bottom: padding, left: padding },
            'bottom-center': { bottom: padding, left: '50%', transform: 'translateX(-50%)' },
            'bottom-right': { bottom: padding, right: padding }
        };

        return {
            ...positionStyles[watermarkPosition],
            height: `${size}px`,
            width: 'auto',
            opacity: watermarkOpacity / 100
        };
    };

    // Kaydet
    const handleSave = async () => {
        if (!watermarkPreview || !watermarkFile) {
            alert('❌ Önce bir logo seçmelisiniz');
            return;
        }

        setIsProcessing(true);

        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            const img = imageRef.current;
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;

            // Ana resmi çiz
            ctx.drawImage(img, 0, 0);

            // Watermark'ı çiz
            const watermark = watermarkImageRef.current;
            if (watermark) {
                ctx.globalAlpha = watermarkOpacity / 100;

                const wSize = (watermarkSize / 100) * Math.min(canvas.width, canvas.height) * 0.15;
                const aspectRatio = watermark.naturalWidth / watermark.naturalHeight;
                const wWidth = wSize * aspectRatio;
                const wHeight = wSize;

                const padding = 20;
                let x, y;

                switch (watermarkPosition) {
                    case 'top-left':
                        x = padding; y = padding;
                        break;
                    case 'top-center':
                        x = (canvas.width - wWidth) / 2; y = padding;
                        break;
                    case 'top-right':
                        x = canvas.width - wWidth - padding; y = padding;
                        break;
                    case 'center-left':
                        x = padding; y = (canvas.height - wHeight) / 2;
                        break;
                    case 'center':
                        x = (canvas.width - wWidth) / 2; y = (canvas.height - wHeight) / 2;
                        break;
                    case 'center-right':
                        x = canvas.width - wWidth - padding; y = (canvas.height - wHeight) / 2;
                        break;
                    case 'bottom-left':
                        x = padding; y = canvas.height - wHeight - padding;
                        break;
                    case 'bottom-center':
                        x = (canvas.width - wWidth) / 2; y = canvas.height - wHeight - padding;
                        break;
                    case 'bottom-right':
                    default:
                        x = canvas.width - wWidth - padding; y = canvas.height - wHeight - padding;
                        break;
                }

                ctx.drawImage(watermark, x, y, wWidth, wHeight);
                ctx.globalAlpha = 1;
            }

            const fileName = mediaFile?.name || `watermarked_${Date.now()}.png`;
            const fileType = mediaFile?.type || 'image/png';

            canvas.toBlob((blob) => {
                const processedFile = new File([blob], fileName, {
                    type: fileType,
                    lastModified: Date.now()
                });

                onSave(processedFile, canvas.toDataURL());
                setIsProcessing(false);
                onClose();
            }, fileType, 0.95);

        } catch (error) {
            console.error('Save error:', error);
            alert('❌ Watermark eklenirken bir hata oluştu');
            setIsProcessing(false);
        }
    };

    if (!isOpen) return null;

    const imageUrl = loadedImageData || mediaPreview?.url || mediaPreview;

    if (!imageUrl) {
        return (
            <div className="mw-modal-overlay" onClick={onClose}>
                <div className="mw-modal-container mw-modal-error" onClick={(e) => e.stopPropagation()}>
                    <div className="mw-error-content">
                        <span className="mw-error-icon">⚠️</span>
                        <p>Resim yüklenemedi</p>
                        <button onClick={onClose} className="mw-btn mw-btn-secondary">Kapat</button>
                    </div>
                </div>
            </div>
        );
    }

    // Pozisyon grid
    const positions = [
        { key: 'top-left', label: 'Sol Üst' },
        { key: 'top-center', label: 'Orta Üst' },
        { key: 'top-right', label: 'Sağ Üst' },
        { key: 'center-left', label: 'Sol Orta' },
        { key: 'center', label: 'Orta' },
        { key: 'center-right', label: 'Sağ Orta' },
        { key: 'bottom-left', label: 'Sol Alt' },
        { key: 'bottom-center', label: 'Orta Alt' },
        { key: 'bottom-right', label: 'Sağ Alt' }
    ];

    const currentPositionLabel = positions.find(p => p.key === watermarkPosition)?.label || 'Sağ Alt';

    return (
        <div className="mw-modal-overlay" onClick={onClose}>
            <div className="mw-modal-container" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="mw-modal-header">
                    <div className="mw-header-title">
                        <span className="mw-header-icon">🖼️</span>
                        <h3>Resim Logo Ekle</h3>
                    </div>
                    <button className="mw-close-btn" onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="mw-modal-content">
                    {/* Sol Panel - Resim Önizleme */}
                    <div className="mw-preview-panel" ref={containerRef}>
                        <div className="mw-image-wrapper">
                            <img
                                ref={imageRef}
                                src={imageUrl}
                                className="mw-image"
                                onLoad={handleImageLoad}
                                alt="Preview"
                            />

                            {/* Logo Overlay */}
                            {watermarkPreview && (
                                <div
                                    className={`mw-logo-overlay mw-pos-${watermarkPosition}`}
                                    style={{ opacity: watermarkOpacity / 100 }}
                                >
                                    <img
                                        ref={watermarkImageRef}
                                        src={watermarkPreview}
                                        alt="Logo"
                                        style={{
                                            height: `${Math.max(20, watermarkSize * 0.3)}px`,
                                            width: 'auto'
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        {imageDimensions.width > 0 && (
                            <div className="mw-image-info">
                                {imageDimensions.width} × {imageDimensions.height}
                            </div>
                        )}
                    </div>

                    {/* Sağ Panel - Ayarlar */}
                    <div className="mw-settings-panel">
                        {/* Logo Seçimi */}
                        <div className="mw-section">
                            <div className="mw-section-header">
                                <span className="mw-section-icon">🖼️</span>
                                <span className="mw-section-title">Logo Seç</span>
                            </div>

                            <div className="mw-logo-selector">
                                <input
                                    ref={iconInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleIconSelect}
                                    id="mw-logo-input"
                                    hidden
                                />

                                {!watermarkPreview ? (
                                    <label htmlFor="mw-logo-input" className="mw-logo-dropzone">
                                        <div className="mw-dropzone-icon">
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M12 5v14M5 12h14" />
                                            </svg>
                                        </div>
                                        <span className="mw-dropzone-text">Logo seçmek için tıklayın</span>
                                        <span className="mw-dropzone-hint">PNG, JPG, SVG</span>
                                    </label>
                                ) : (
                                    <div className="mw-logo-preview">
                                        <img src={watermarkPreview} alt="Seçilen Logo" />
                                        <button
                                            className="mw-logo-remove"
                                            onClick={handleRemoveIcon}
                                            title="Logoyu Kaldır"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M18 6L6 18M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Kayıtlı Markalar */}
                        {brands.length > 0 && (
                            <div className="mw-section">
                                <div className="mw-section-header">
                                    <span className="mw-section-icon">🏷️</span>
                                    <span className="mw-section-title">Kayıtlı Markalar</span>
                                </div>

                                <div className="mw-brands-list">
                                    {loadingBrands ? (
                                        <div className="mw-brands-loading">
                                            <span>Yükleniyor...</span>
                                        </div>
                                    ) : (
                                        brands.map(brand => (
                                            <div key={brand.id} className="mw-brand-item">
                                                <span className="mw-brand-name">{brand.name}</span>
                                                <div className="mw-brand-logos">
                                                    {brand.logo_dark_url && (
                                                        <button
                                                            className={`mw-brand-logo-btn dark ${selectedBrandLogo?.brandId === brand.id &&
                                                                    selectedBrandLogo?.type === 'dark' ? 'selected' : ''
                                                                }`}
                                                            onClick={() => handleBrandLogoSelect(brand, 'dark')}
                                                            title="Koyu arka plan logosu"
                                                        >
                                                            <img
                                                                src={`${BASE_URL}${brand.logo_dark_url}`}
                                                                alt={`${brand.name} - Koyu`}
                                                            />
                                                        </button>
                                                    )}
                                                    {brand.logo_light_url && (
                                                        <button
                                                            className={`mw-brand-logo-btn light ${selectedBrandLogo?.brandId === brand.id &&
                                                                    selectedBrandLogo?.type === 'light' ? 'selected' : ''
                                                                }`}
                                                            onClick={() => handleBrandLogoSelect(brand, 'light')}
                                                            title="Açık arka plan logosu"
                                                        >
                                                            <img
                                                                src={`${BASE_URL}${brand.logo_light_url}`}
                                                                alt={`${brand.name} - Açık`}
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Pozisyon Seçimi */}
                        {watermarkPreview && (
                            <>
                                <div className="mw-section">
                                    <div className="mw-section-header">
                                        <span className="mw-section-icon">📍</span>
                                        <span className="mw-section-title">Pozisyon</span>
                                        <span className="mw-section-value">{currentPositionLabel}</span>
                                    </div>

                                    <div className="mw-position-grid">
                                        {positions.map(pos => (
                                            <button
                                                key={pos.key}
                                                className={`mw-position-btn ${watermarkPosition === pos.key ? 'active' : ''}`}
                                                onClick={() => setWatermarkPosition(pos.key)}
                                                title={pos.label}
                                            >
                                                <span className="mw-position-dot"></span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Boyut */}
                                <div className="mw-section">
                                    <div className="mw-section-header">
                                        <span className="mw-section-icon">📏</span>
                                        <span className="mw-section-title">Boyut</span>
                                        <span className="mw-section-value">{watermarkSize}%</span>
                                    </div>

                                    <div className="mw-slider-container">
                                        <input
                                            type="range"
                                            min="30"
                                            max="200"
                                            value={watermarkSize}
                                            onChange={(e) => setWatermarkSize(Number(e.target.value))}
                                            className="mw-slider"
                                        />
                                        <div className="mw-slider-labels">
                                            <span>Küçük</span>
                                            <span>Büyük</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Opaklık */}
                                <div className="mw-section">
                                    <div className="mw-section-header">
                                        <span className="mw-section-icon">💎</span>
                                        <span className="mw-section-title">Opaklık</span>
                                        <span className="mw-section-value">{watermarkOpacity}%</span>
                                    </div>

                                    <div className="mw-slider-container">
                                        <input
                                            type="range"
                                            min="10"
                                            max="100"
                                            value={watermarkOpacity}
                                            onChange={(e) => setWatermarkOpacity(Number(e.target.value))}
                                            className="mw-slider"
                                        />
                                        <div className="mw-slider-labels">
                                            <span>Şeffaf</span>
                                            <span>Opak</span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="mw-modal-footer">
                    <button className="mw-btn mw-btn-secondary" onClick={onClose}>
                        İptal
                    </button>
                    <button
                        className="mw-btn mw-btn-primary"
                        onClick={handleSave}
                        disabled={!watermarkPreview || isProcessing}
                    >
                        {isProcessing ? (
                            <>
                                <svg className="mw-spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 12a9 9 0 11-6.219-8.56" />
                                </svg>
                                İşleniyor...
                            </>
                        ) : (
                            <>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                                    <polyline points="17,21 17,13 7,13 7,21" />
                                    <polyline points="7,3 7,8 15,8" />
                                </svg>
                                Kaydet
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default MediaWatermark;